import { CHOICES, ARTIFACT_EARLY_VALUE, ARTIFACT_LATE_VALUE, ARTIFACTS_AT_EARLY_VALUE } from '../constants';
import { cardById, isTreasure, isHazard } from '../deck';
import { playersInTemple, gemsOnPath } from '../actions/roundActions';

// The computer players.
//
// Incan Gold is the rare push-your-luck game where the odds are genuinely
// computable at the table, so these bots compute them rather than guessing.
// Everything below is derived from PUBLIC information only - see
// `unseenCards`, which is the one piece of real restraint in this file.

/**
 * What is still face down, as a multiset of card ids.
 *
 * Deliberately reconstructed from `questRemaining + artifactsInPlay` minus
 * what is lying on the path, rather than read off `state.deck`. Both give the
 * same SET, but `state.deck` is in shuffled ORDER, and a bot that touched it
 * could - by accident as easily as by design - peek at the next card. Deriving
 * it makes that impossible rather than merely forbidden.
 *
 * Everything used here is public at a real table: the deck's composition is
 * printed on the box, the removed hazards sit face up under the temple cards,
 * and the path is face up in front of everyone.
 */
export const unseenCards = (state) => {
  const onPath = new Set(state.path.map((c) => c.id));
  return [...state.questRemaining, ...state.artifactsInPlay]
    .filter((id) => !onPath.has(id))
    .map((id) => cardById(id));
};

/**
 * The chance the next card ends the round: how many of the face-down cards
 * are a second copy of a hazard already showing.
 */
export const bustChance = (state) => {
  const unseen = unseenCards(state);
  if (!unseen.length) return 0;
  const deadly = unseen.filter((c) => isHazard(c) && (state.hazardsSeen[c.hazard] || 0) >= 1);
  return deadly.length / unseen.length;
};

/** What one more card is worth to me, if it doesn't kill us. */
export const expectedShare = (state, inTempleCount) => {
  const unseen = unseenCards(state);
  if (!unseen.length || inTempleCount <= 0) return 0;
  const total = unseen.reduce((sum, card) => (
    sum + (isTreasure(card) ? Math.floor(card.value / inTempleCount) : 0)
  ), 0);
  return total / unseen.length;
};

/** What the artifacts on the path would be worth to whoever leaves alone. */
export const artifactWorthOnPath = (state) => {
  const taken = state.artifactsTaken;
  return state.artifactsOnPath.reduce((sum, _id, i) => (
    sum + (taken + i < ARTIFACTS_AT_EARLY_VALUE ? ARTIFACT_EARLY_VALUE : ARTIFACT_LATE_VALUE)
  ), 0);
};

/**
 * A blunt prior for "will anyone else bail this turn?".
 *
 * There is no way to know, and modelling opponents properly is a rabbit hole
 * for a game this size. One number, used for two things: how thin a leaver's
 * share of the path is likely to be, and how likely it is that a bot walking
 * out with an artifact actually gets to keep it.
 */
export const LEAVE_PRIOR = 0.25;

/**
 * Three temperaments.
 *
 * `riskWeight` scales the true bust chance before a bot plans against it - a
 * wary bot believes a 20% chance is really 30% and gets out early, a reckless
 * one talks itself into 13% and goes one card too far. `leavePrior` is how
 * crowded it expects the exit to be, which sets how much it thinks a share of
 * the path is worth and how likely it is to walk out alone with an artifact.
 *
 * WHAT THE SWEEP ACTUALLY FOUND (`yarn tune-bots`), because it is not what
 * the code was written expecting:
 *
 * 1. BOTH knobs are strength axes, not style axes. There is no orthogonal
 *    dial hiding in here the way `reserve` was hiding in no-thanks. Win rate
 *    against a mixed field has a shallow optimum in riskWeight around 0.55-
 *    0.85 and falls away on both sides.
 *
 * 2. Being DIFFERENT from the rest of the table is itself worth points, which
 *    is a fact about Incan Gold rather than about these bots. A lone leaver
 *    takes the whole path and every artifact on it; two players leaving
 *    together split the path and get no artifact at all. So any setting that
 *    decorrelates a bot from the field earns real money, and the leavePrior
 *    sweep comes out U-shaped - both extremes beat the middle - purely
 *    because both extremes are unlike everyone else.
 *
 * 3. The pathological case is a table of IDENTICAL bots, and it is very
 *    pathological: they choose identically every turn, leave in a single
 *    block, split everything, and take ZERO artifacts across 6,000 games.
 *    That is why `tuningFor` jitters by seat below, and why the sweep draws
 *    every seat independently instead of testing one candidate against a
 *    baseline of clones.
 *
 * So these three are honestly three near-peak settings, chosen to play
 * visibly differently rather than to be three difficulty levels. Their
 * average scores land within ~2.5 points of each other; their win rates
 * differ by more than that, and point 2 above is most of why.
 */
export const PERSONALITIES = {
  cautious: { label: 'Wary', riskWeight: 0.85, leavePrior: 0.22 },
  steady: { label: 'Steady', riskWeight: 0.6, leavePrior: 0.15 },
  daring: { label: 'Reckless', riskWeight: 0.4, leavePrior: 0.08 },
};

/**
 * The same temperament, nudged so that two bots of one type are never twins.
 *
 * Up to seven bots can sit down and there are only three temperaments, so
 * without this a six-bot table contains two or three exact clones - and per
 * point 3 above, clones move in lockstep and can never take an artifact
 * between them. The offsets are deterministic (no RNG in engine code) and
 * small enough not to move a bot off its tuned setting, but they are enough
 * to break a tie on the turn it matters.
 */
const SEAT_NUDGE = [0, 0.06, -0.06, 0.12, -0.12, 0.03, -0.03, 0.09];

export const tuningFor = (personality, botIndex = 0) => {
  const base = PERSONALITIES[personality] || PERSONALITIES.steady;
  const nudge = SEAT_NUDGE[botIndex % SEAT_NUDGE.length];
  return {
    ...base,
    riskWeight: base.riskWeight * (1 + nudge),
    leavePrior: Math.max(0, base.leavePrior + nudge * 0.4),
  };
};

export const PERSONALITY_ORDER = ['steady', 'cautious', 'daring'];
export const personalityFor = (index) => PERSONALITY_ORDER[index % PERSONALITY_ORDER.length];

/**
 * What a bot thinks it will walk out with if it stays for `k` more cards and
 * then leaves.
 *
 * Surviving k more draws is (1-p)^k, and each survived draw is worth a share
 * of an average card. Everything already carried - pocket, the path share,
 * the artifacts it would collect on the way out - rides along and is lost
 * together if any one of those k cards bites.
 *
 * `p` is held constant across the k cards, which is a simplification in the
 * bot's favour early (the deck gets more dangerous as hazards accumulate) and
 * against it late. It is re-evaluated from scratch every single turn, so the
 * error never compounds.
 */
export const valueOfStaying = ({ carried, perCard, pBust, k }) => (
  (1 - pBust) ** k * (carried + perCard * k)
);

/**
 * Torch or camp.
 *
 * The bot picks the depth it would ideally play to - "how many more cards is
 * it worth staying for?" - and then does the first step of that plan. If the
 * best plan is to stay for zero more cards, it leaves.
 *
 * This is what makes it play like a person rather than like a spreadsheet:
 *
 *  - What is at risk is the POCKET, never the tent. Early in a round the
 *    pocket is nearly empty and pushing on is almost free, so everyone dives
 *    in; late it is heavy and the same odds feel completely different. The
 *    formula reproduces that without being told to.
 *  - Leaving does not only bank the pocket, it sweeps the path - and the path
 *    is worth more the fewer people are expected to walk out alongside.
 *  - The artifacts sit on BOTH sides of the comparison, because continuing
 *    does not forfeit them: they stay on the path, still waiting for whoever
 *    eventually leaves alone. An earlier version had them only on the leave
 *    side, and the result was that every bot bolted for the door the instant
 *    an artifact appeared - which, since a lone leaver is required, meant
 *    nobody got it and the whole round was thrown away.
 */
export const decideFor = (state, player, personality = 'steady') => {
  const { riskWeight, leavePrior } = tuningFor(personality, player.botIndex ?? player.seat ?? 0);
  const inside = playersInTemple(state);
  const n = inside.length;

  const pBust = Math.min(1, bustChance(state) * riskWeight);

  // If I leave: my pocket, plus a share of the path split with however many
  // others are expected to walk out at the same time.
  const expectedLeavers = 1 + (n - 1) * leavePrior;
  const pathShare = gemsOnPath(state) / expectedLeavers;
  const soloChance = (1 - leavePrior) ** Math.max(0, n - 1);
  const artifactValue = artifactWorthOnPath(state) * soloChance;

  const carried = player.pocket + pathShare + artifactValue;
  const perCard = expectedShare(state, n);

  // Can't stay for more cards than there are.
  const maxDepth = Math.max(0, unseenCards(state).length);
  let best = 0;
  let bestValue = carried; // k = 0, i.e. leave now
  for (let k = 1; k <= maxDepth; k += 1) {
    const value = valueOfStaying({ carried, perCard, pBust, k });
    if (value > bestValue) {
      bestValue = value;
      best = k;
    }
    // (1-p)^k decays and the linear term can't catch it up once it has
    // turned over, so the first decline is the peak.
    if (value < bestValue) break;
  }

  return best >= 1 ? CHOICES.CONTINUE : CHOICES.LEAVE;
};

// ---------------------------------------------------------------------------
// SWEEP RESULTS - `yarn tune-bots --games 8000`. Every seat draws its setting
// independently each game (see the script for why a baseline-of-clones sweep
// is worthless here). "win%" shares a tie between the tied players.
//
// The three shipped settings against each other in a mixed field:
//
//                3 players        4 players        6 players
//   risk/leave   win%   avg      win%   avg       win%   avg
//   0.85/0.22    37.8   36.5     27.3   27.1      18.3   16.3
//   0.60/0.15    31.9   35.0     21.2   25.9      11.8   15.2
//   0.40/0.08    30.2   33.8     26.5   27.2      19.9   18.1
//
// Average score - the fairer measure of strength - spans 2.7 / 1.3 / 2.9
// points. Win rate spans more, and the reason is the decorrelation effect in
// point 2 above rather than one bot being better at the game.
//
// ONE HONEST WEAKNESS, recorded rather than smoothed over: at an all-bot
// table the middle setting is squeezed. Playing 1 human + 3 bots, Steady wins
// 8.2% and 4.3% from its two seats while Wary and Reckless win 42.7% and
// 44.9%. Steady sits between the other two and is therefore the most likely
// of the three to leave in company - and leaving in company is exactly what
// costs you the path and the artifact. It is not that Steady plays badly; it
// is that "average" is the worst thing to be in a game whose prize goes to
// whoever is unlike everyone else.
//
// The seat nudge is doing its job: two Steady bots at one table used to be
// exact clones and took 0.00 artifacts per game across 6,000 games. They now
// take 0.10 and 0.05.
